import type { EventDto, EventStaffMemberDto } from "@/entities/event/types"
import {
  getEventRegistrationWindowStatus,
  isCurrentUserEventInitiator,
} from "@/entities/event/eventDerivedStatuses"

const POLICY_APPROVE_REGISTRATION = "LeninkaArt.Events.ApproveRegistration"

const hasPolicy = (policies: Record<string, boolean> | null | undefined, key: string) =>
  Boolean(policies?.[key])

const sameId = (a: string | null | undefined, b: string | null | undefined) => {
  if (!a || !b) return false
  return String(a).trim() === String(b).trim()
}

const findStaffEntry = (
  event: Pick<EventDto, "staff">,
  userId: string | null | undefined,
): EventStaffMemberDto | null => {
  if (!userId) return null
  const staff = event.staff ?? []
  for (const s of staff) {
    if (sameId(s?.userId, userId)) return s
  }
  return null
}

/** Пользователь указан в персонале мероприятия (не считая глобальные политики). */
export const isCurrentUserOnEventStaffList = (
  event: Pick<EventDto, "staff">,
  userId: string | null | undefined,
): boolean => findStaffEntry(event, userId) !== null

/**
 * Модерация заявок на карточке предпросмотра в портале — только контекст мероприятия (инициатор или персонал с флагом).
 * Глобальная политика `ApproveRegistration` не учитывается: такие пользователи работают через вкладку «Модерация регистраций».
 */
export const canModerateEventRegistrations = (
  event: Pick<EventDto, "creatorId" | "staff">,
  userId: string | null | undefined,
): boolean => {
  if (isCurrentUserEventInitiator(event, userId)) return true
  const staffEntry = findStaffEntry(event, userId)
  return Boolean(staffEntry?.canModerateRegistrations)
}

/** Отметка посещения и снятие ошибочной отметки — инициатор или персонал с `canCheckIn`. */
export const canCheckInEventParticipants = (
  event: Pick<EventDto, "creatorId" | "staff">,
  userId: string | null | undefined,
): boolean => {
  if (isCurrentUserEventInitiator(event, userId)) return true
  const staffEntry = findStaffEntry(event, userId)
  return Boolean(staffEntry?.canCheckIn)
}

/** Просмотр списка участников на предпросмотре — инициатор или любой из персонала (без глобальной политики). */
export const canViewEventParticipants = (
  event: Pick<EventDto, "creatorId" | "staff">,
  userId: string | null | undefined,
): boolean => {
  if (isCurrentUserEventInitiator(event, userId)) return true
  return findStaffEntry(event, userId) !== null
}

/**
 * Вкладка «Модерация регистраций»: список строится из `GET my-manageable-events` (опубликовано), чтобы совпадать
 * с доступом к `GET /event/{id}`. Фильтр: опубликовано + регистрация ещё не закрыта по датам + (глобальная политика
 * модерации заявок ИЛИ персонал с `canModerateRegistrations`). Правило «просто инициатор» здесь не используется.
 */
export const isEventShownOnRegistrationModerationTab = (
  event: Pick<EventDto, "status" | "creatorId" | "staff" | "registrationStartTime" | "registrationEndTime">,
  userId: string | null | undefined,
  grantedPolicies?: Record<string, boolean> | null,
): boolean => {
  if (Number(event.status) !== 2) return false
  if (getEventRegistrationWindowStatus(event.registrationStartTime, event.registrationEndTime) === 2) return false
  if (hasPolicy(grantedPolicies, POLICY_APPROVE_REGISTRATION)) return true
  if (userId === undefined || userId === null || String(userId).trim() === "") return false
  const staffEntry = findStaffEntry(event, userId)
  return Boolean(staffEntry?.canModerateRegistrations)
}
